/**
 * Accessibility fix for Flickity carousels
 *
 * Issue: Flickity adds aria-hidden="true" to inactive slides, but doesn't
 * remove focusable elements from keyboard tab order. This causes screen reader
 * users to tab to elements they can't see and hear nothing.
 *
 * Solution: When a slide has aria-hidden="true", set all focusable elements
 * inside it to tabindex="-1" to remove them from tab order.
 *
 * WCAG 2.1 Level A - 4.1.2 Name, Role, Value
 */

(function() {
  'use strict';

  // Selectors for focusable elements
  var FOCUSABLE_SELECTORS = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    'area[href]',
    'iframe',
    'object',
    'embed',
    '[contenteditable]'
  ].join(', ');

  /**
   * Update focusable state of elements in a Flickity cell
   * @param {Object} cell - Flickity cell object
   * @param {boolean} isSelected - Whether the cell is currently selected
   */
  function updateCellFocusableElements(cell, isSelected) {
    var focusableElements = cell.element.querySelectorAll(FOCUSABLE_SELECTORS);

    focusableElements.forEach(function(el) {
      if (isSelected) {
        // Slide is visible - restore original tabindex
        var originalTabindex = el.getAttribute('data-original-tabindex');
        if (originalTabindex !== null) {
          if (originalTabindex === 'null') {
            el.removeAttribute('tabindex');
          } else {
            el.setAttribute('tabindex', originalTabindex);
          }
          el.removeAttribute('data-original-tabindex');
        }
      } else {
        // Slide is hidden - save current tabindex and set to -1
        var currentTabindex = el.getAttribute('tabindex');
        if (currentTabindex !== '-1') {
          // Store original tabindex (or 'null' if it didn't have one)
          el.setAttribute('data-original-tabindex', currentTabindex || 'null');
          el.setAttribute('tabindex', '-1');
        }
      }
    });
  }

  /**
   * Initialize accessibility fix for a Flickity instance
   * @param {HTMLElement} elem - The Flickity container element
   */
  function initFlickityA11yFix(elem) {
    // Wait a bit for Flickity to fully initialize
    setTimeout(function() {
      var flkty = Flickity.data(elem);

      if (!flkty) {
        console.warn('Flickity A11y Fix: Could not find Flickity instance on', elem);
        return;
      }

      /**
       * Update all cells' focusable elements based on selection state
       */
      function updateAllCells() {
        flkty.cells.forEach(function(cell, index) {
          var isSelected = index === flkty.selectedIndex;
          updateCellFocusableElements(cell, isSelected);
        });
      }

      // Run on initialization
      updateAllCells();

      // Run on every slide change
      flkty.on('change', function() {
        updateAllCells();
      });

      // Run when cells are added/removed (e.g., for dynamic content)
      flkty.on('cellSelect', function() {
        updateAllCells();
      });

    }, 100); // Small delay to ensure Flickity is fully initialized
  }

  /**
   * Find and fix all Flickity instances on the page
   */
  function initAllFlickityFixes() {
    // Find all elements that have been initialized with Flickity
    var flickityElements = document.querySelectorAll('.flickity-enabled');

    if (flickityElements.length === 0) {
      // No Flickity instances found yet, try again after a delay
      setTimeout(initAllFlickityFixes, 500);
      return;
    }

    flickityElements.forEach(function(elem) {
      initFlickityA11yFix(elem);
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllFlickityFixes);
  } else {
    // DOM already loaded
    initAllFlickityFixes();
  }

  // Also initialize on theme:loaded event if it exists (for Shopify themes)
  document.addEventListener('theme:loaded', function() {
    setTimeout(initAllFlickityFixes, 300);
  });

  // Make the function globally available for manual initialization if needed
  window.initFlickityA11yFix = initFlickityA11yFix;

})();
